import {
  Component,
  ChangeDetectionStrategy,
  NgZone,
  ElementRef,
  ViewEncapsulation,
  OnDestroy,
  AfterViewChecked,
  inject,
  AfterViewInit,
  OnInit,
  input,
  viewChild,
} from '@angular/core';
import * as L from 'leaflet';
import { ImageOverlay } from 'leaflet';
import { distinctUntilChanged, of, Subject, switchMap, takeUntil } from 'rxjs';
import { ResourceService } from '@assist/shared/data';
import { AssistGeodataBM, NavigationService } from '@cue/navigation-services';
import { explicitEffect } from 'ngxtension/explicit-effect';
import { LazyStylesService } from '@cue/lazy-styles';
import { loadNavigationLeafletStyles } from '@cue/navigation-widgets';

@Component({
  selector: 'assist-new-resource-map',
  templateUrl: './new-resource-map.component.html',
  styleUrls: ['./new-resource-map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [],
})
export class NewResourceMapComponent implements AfterViewChecked, OnDestroy, AfterViewInit, OnInit {
  private resourceService = inject(ResourceService);
  private zone = inject(NgZone);
  private navigationService = inject(NavigationService);
  private lazyStyles = inject(LazyStylesService);

  breadcrumbContainer = viewChild('breadcrumbContainer', { read: ElementRef });
  resourceId = input();

  areaId$: Subject<number> = new Subject<number>();

  areaId = input<number>();
  setAreaId = explicitEffect([this.areaId], ([areaId]) => {
    this.areaId$.next(areaId);
  });

  imageSrc = input<string>();

  mapElement = viewChild('mapElement', { read: ElementRef });
  private map: any;
  private imageOverlay!: ImageOverlay;
  private renderedLayers = new L.FeatureGroup([], {});
  reset$ = new Subject();

  async ngAfterViewInit(): Promise<void> {
    await loadNavigationLeafletStyles(this.lazyStyles);
    this.initMap();
  }

  ngAfterViewChecked() {
    if (this.breadcrumbContainer()) {
      this.breadcrumbContainer()!.nativeElement.scrollLeft = this.breadcrumbContainer()!.nativeElement.scrollWidth;
    }
  }

  _geo?: any;

  private createNewLayer(areaGeoData: AssistGeodataBM) {
    const geoJson = JSON.parse(areaGeoData.geometry) as any;
    if (geoJson.properties == null) {
      geoJson.properties = {};
    }
    if (areaGeoData.typeId == 2) {
      geoJson.properties.resourceId = areaGeoData.resourceId;
    }

    this._geo = L.geoJSON(geoJson, {
      style: {
        color: 'orange',
        fill: true,
        fillOpacity: 0.6,
        fillColor: 'orange',
      },
      pointToLayer: (feature, latlng) => {
        if (areaGeoData.radius) {
          return new L.Circle(latlng, areaGeoData.radius);
        } else {
          return new L.Marker(latlng, {});
        }
      },
    });
    this._geo.addTo(this.renderedLayers);

    const currentMaxBounds = this.map.options.maxBounds;
    this.map.setMaxBounds(null);
    this.map.invalidateSize();
    this.map.fitBounds(this._geo.getBounds(), {
      animate: false,
      padding: [30, 30],
    });
    this.map.setMaxBounds(currentMaxBounds);
  }

  private paintLayers(geoData) {
    if (geoData.loading) return;
    const existingLayers = this.renderedLayers
      .getLayers()
      .filter((x: any) => {
        if (x.getLayers) {
          return x.getLayers()[0];
        }
        return false;
      })
      .map((x: any) => x.getLayers()[0]);

    geoData.data.forEach((assistGeoData) => {
      if (assistGeoData.resourceId === this.resourceId()) {
        this.createNewLayer(assistGeoData);
      }
    });
  }

  private prepareMapAndLayers() {
    this.zone.runOutsideAngular(() => {
      if (!this.map) {
        this.map = L.map(this.mapElement().nativeElement, {
          preferCanvas: false,
          center: [0, 0],
          attributionControl: false,
          zoom: 1,
          minZoom: 0,
          maxZoom: 8,
          crs: L.CRS.Simple,
        } as any) as any;
      }
      this.map.addLayer(this.renderedLayers);
    });
  }

  private initMap() {
    this.prepareMapAndLayers();
    if (this.imageSrc()) {
      this.setImageMap(this.imageSrc()!);
    }
    this.areaId$
      .pipe(
        distinctUntilChanged(),
        switchMap((areaId) => this.navigationService.getGeodataForAreaId(areaId)),
        takeUntil(this.reset$)
      )
      .subscribe((geodata) => {
        this.paintLayers(geodata);
      });
    this.map.invalidateSize();
    return of(null);
  }

  private setImageMap(imageSrc: string) {
    if (imageSrc) {
      const foundArea = this.resourceService.getAreaSyncByImageUrl(imageSrc);

      const southWest = this.map.unproject([0, foundArea!.height], 2);
      const northEast = this.map.unproject([foundArea!.width, 0], 2);
      const imageBounds = new L.LatLngBounds(southWest, northEast);
      if (this.imageOverlay) {
        this.imageOverlay.setUrl(imageSrc);
        this.imageOverlay.setBounds(imageBounds);
      } else {
        this.imageOverlay = L.imageOverlay(imageSrc, imageBounds, {
          zIndex: -1,
        }).addTo(this.map);
      }
      this.map.fitBounds(this.imageOverlay.getBounds(), {
        animate: false,
      });
      this.map.invalidateSize();
      const areaBounds = new L.LatLngBounds(
        this.map.unproject([0 - foundArea!.height / 3, foundArea!.height + foundArea!.height / 3], 2),
        this.map.unproject([foundArea!.width + foundArea!.width / 3, 0 - foundArea!.width / 3], 2)
      );
      this.map.setMaxBounds(imageBounds);
      this.map.fitBounds(areaBounds);
    }
  }

  // host element resize
  private resizeMap() {
    if (this.map) {
      this.map.invalidateSize();

      if (this._geo) {
        this.map.fitBounds(this._geo.getBounds(), {
          animate: false,
          padding: [30, 30],
        });
      }
    }
  }

  _resizeObserver = new ResizeObserver(() => {
    this.resizeMap();
  });

  ngOnInit() {
    this.areaId$
      .pipe(
        distinctUntilChanged(),
        switchMap((areaId) => this.navigationService.getGeodataForAreaId(areaId)),
        takeUntil(this.reset$)
      )
      .subscribe((geodata) => {
        this.paintLayers(geodata);
      });
    this._resizeObserver.observe(this.mapElement().nativeElement);
  }

  ngOnDestroy(): void {
    this._resizeObserver.disconnect();
    this.reset$.next(null);
  }
}
